import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// Actions
import {
  closeCampaignMediaCreativesForm,
  deleteCreativesFileItems,
  getMediaDetails,
} from "../../../actions/campaign-media/CampaignMediaActions";

// Constants and Utils
import {
  FileTypeConstants,
  FormDataTargets,
} from "../../../constants/GeneralConstants";

// Components
import CheckBox from "../../../components/check-box/CheckBox";
import Spinner from "../../../components/spinner/Spinner";

// Page Components
function CreativeItemsSection({
  creativeItems = [],
  selectedItems = [],
  setSelectedItems = () => {},
}) {
  // check for creatives
  const isCreativesPresent = creativeItems.length > 0;

  // no data
  if (!isCreativesPresent) {
    return (
      <h6 className="font-italic text-center my-4 text-muted">
        No Creative Present For This Media
      </h6>
    );
  }

  // function
  function handleSelectCreative(file) {
    const { type, filename } = file;
    const constructItem = {
      type,
      fileName: filename,
    };

    // check is selected already
    const isSelected = selectedItems.some(
      (selected) => selected.fileName === filename && selected.type === type
    );

    // remove from selectedItems
    if (isSelected) {
      const filteredItems = selectedItems.filter(
        (selected) =>
          !(selected.fileName === filename && selected.type === type)
      );

      setSelectedItems(filteredItems);
      return;
    }

    // add to selectedItems
    setSelectedItems((prev) => [...prev, constructItem]);
  }

  return (
    <div className="row max-h-600 overflow-y-auto">
      {creativeItems.map((file, i) => {
        const { type, filename, originalUrl } = file;

        // constants
        const { image, video } = FileTypeConstants;

        // check type
        const isImage = type === image;
        const isVideo = type === video;

        const isChecked = selectedItems.some(
          (selected) => selected.fileName === filename && selected.type === type
        );

        const checkedTextStyle = isChecked ? "text-primary" : "";
        const checkedContainerStyle = isChecked
          ? "border border-primary p-2 bg-alt rounded-lg"
          : "";

        return (
          <div key={i} className="col-6 border-bottom pb-2 mb-2">
            <div className="d-flex align-items-center">
              <CheckBox
                boxStyle="mb-0 p-0"
                inputBoxStyle="m-0"
                checkBoxKey={filename}
                showLabel={false}
                checked={isChecked}
                onChange={() => handleSelectCreative(file)}
              />
              <span
                className={`ml-2 text-truncate ${checkedTextStyle}`}
                title={filename}
              >
                {filename}
              </span>
            </div>

            {/* image */}
            {isImage && (
              <img
                className={`creative-cont ${checkedContainerStyle}`}
                src={originalUrl}
              />
            )}

            {/* video */}
            {isVideo && (
              <video
                className={`creative-cont ${checkedContainerStyle}`}
                controls
                autoPlay={false}
                muted
              >
                <source src={originalUrl} type="video/mp4" />
              </video>
            )}
          </div>
        );
      })}
    </div>
  );
}

function MediaCreativesFormBody({
  mediaDetails = {},
  selectedItems = [],
  setSelectedItems,
}) {
  const dispatch = useDispatch();

  // data
  const {
    campaignId,
    id,
    title,
    sourceId,
    creativeItems = [],
  } = mediaDetails || {};
  const selectedItemCount = selectedItems?.length || 0;

  // delete loading
  const creativeDeleteLoading = useSelector(
    (state) => state.campaignMedia.creativeDeleteLoading
  );

  // functions
  function handleDeleteCreatives() {
    dispatch(
      deleteCreativesFileItems(campaignId, id, selectedItems, setSelectedItems)
    );
  }

  return (
    <>
      <div className="d-flex align-items-center justify-content-between">
        <div>
          <h5 className="mb-0">{title}</h5>
          <p className="mb-0">DeviceId : {sourceId || "--"}</p>
        </div>

        <button
          type="button"
          className="btn btn-primary"
          onClick={handleDeleteCreatives}
          disabled={!selectedItemCount || creativeDeleteLoading}
        >
          Delete Creatives
          {selectedItemCount > 0 && (
            <span className="mx-2">({selectedItemCount})</span>
          )}
          {creativeDeleteLoading && <Spinner className="spinner-border-sm" />}
        </button>
      </div>
      <hr className="my-2" />

      {/* creative */}
      <CreativeItemsSection
        creativeItems={creativeItems}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
      />
    </>
  );
}

/**
 * Campaign Medias Creatives Form
 */
export default function CampaignMediasCreativesForm() {
  const dispatch = useDispatch();

  // state
  const [selectedItems, setSelectedItems] = useState([]);

  // media details
  const mediaDetailsLoading = useSelector(
    (state) => state.campaignMedia.mediaDetailsLoading
  );
  const mediaDetails =
    useSelector((state) => state.campaignMedia.media.mediaDetails) || {};

  // creatives modal
  const creativesProcessData = useSelector(
    (state) => state.campaignMedia.creativesProcessData
  );
  const { id: mediaId, campaignId } = creativesProcessData || {};

  useEffect(() => {
    dispatch(getMediaDetails(campaignId, mediaId));
  }, [dispatch, campaignId, mediaId]);

  function onCancel() {
    dispatch(closeCampaignMediaCreativesForm());
  }

  return (
    <div
      className="modal fade"
      id={FormDataTargets.campaignMediaCreativesForm}
      data-backdrop="static"
    >
      <div className="modal-dialog modal-xl" role="document">
        <div className="modal-content">
          {/* Form Header */}
          <div className="modal-header bg-alt px-5 align-items-baseline">
            <h4 className="modal-title">Media Creatives</h4>
            <button
              type="button"
              className="btn btn-outline-primary px-3 rounded-lg shadow-none"
              data-dismiss="modal"
              onClick={onCancel}
            >
              {"X"}
            </button>
          </div>

          {/* modal body */}
          <div className="modal-body px-5 py-4 mt-2 min-h-400">
            {mediaDetailsLoading && (
              <div className="d-flex align-items-center justify-content-center bg-alt p-2 border">
                <Spinner />
                <span className="ml-2">Media Details Loading...</span>
              </div>
            )}

            {!mediaDetailsLoading && (
              <MediaCreativesFormBody
                mediaDetails={mediaDetails}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
